import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, BarcodeFormat } from '@zxing/library';
import { useNavigate } from 'react-router-dom';

// ✅ Get allowed airlines for current terminal from localStorage
function getAllowedAirlinesForTerminal() {
  const terminalsData = JSON.parse(localStorage.getItem('terminals')) || [];
  const currentTerminal = localStorage.getItem('terminal');
  if (!currentTerminal || !terminalsData.length) {
    return []; // Return empty array if no data
  }
  // Filter airlines for the current terminal
  const filteredAirlines = terminalsData
    .filter(terminal => terminal.sys_terminal === currentTerminal)
    .map(terminal => terminal.sys_airlinecode);
  return [...new Set(filteredAirlines)]; // Remove duplicates
}

function BarcodePDF417Scanner() {
  const [scannedData, setScannedData] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false); // ✅ for popup
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    flightNo: '',
    sqNo: '',
    date: '',
    time: '',
    airline: '',
  });

  // Get allowed airlines for current terminal
  const allowedAirlines = getAllowedAirlinesForTerminal();

  useEffect(() => {
    codeReaderRef.current = new BrowserMultiFormatReader();
    startScanner();
    return () => {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    };
  }, []);

  const startScanner = async (deviceId = undefined) => {
    setError('');
    setScannedData('');
    setParsedData(null);

    try {
      let constraints = {
        video: {
          facingMode: { exact: 'environment' },
          width: { ideal: 2560 },
          height: { ideal: 1440 },
          focusMode: 'continuous',
          advanced: [{ focusMode: 'continuous' }],
        },
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        console.warn('Exact facingMode failed, retrying:', err);
        constraints = {
          video: {
            facingMode: 'environment',
            width: { ideal: 2560 },
            height: { ideal: 1440 },
            focusMode: 'continuous',
            advanced: [{ focusMode: 'continuous' }],
          },
        };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      // ✅ Apply zoom if supported
      const [videoTrack] = stream.getVideoTracks();
      const capabilities = videoTrack.getCapabilities();
      if (capabilities.zoom) {
        const maxZoom = capabilities.zoom.max || 1;
        const minZoom = capabilities.zoom.min || 1;
        const optimalZoom = Math.min(Math.max(2.0, minZoom), maxZoom);
        try {
          await videoTrack.applyConstraints({ advanced: [{ zoom: optimalZoom }] });
          console.log(`Zoom set to ${optimalZoom}`);
        } catch (zoomErr) {
          console.warn('Failed to apply zoom:', zoomErr);
        }
      }

      const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();
      setCameras(videoInputDevices);

      let useDevice = deviceId;
      if (!useDevice) {
        const rearCam = videoInputDevices.find((cam) =>
          /back|rear|environment/i.test(cam.label.toLowerCase())
        );
        useDevice = rearCam ? rearCam.deviceId : videoInputDevices[0]?.deviceId;
      }
      setSelectedCamera(useDevice);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // ✅ Wait for video to initialize
      await new Promise((resolve) => {
        let attempts = 0;
        const checkVideo = () => {
          attempts++;
          if (videoRef.current?.videoWidth > 0 && videoRef.current?.videoHeight > 0) {
            resolve();
          } else if (attempts > 100) {
            console.warn('Camera slow to initialize, proceeding...');
            resolve();
          } else {
            requestAnimationFrame(checkVideo);
          }
        };
        checkVideo();
      });

      codeReaderRef.current.decodeFromVideoDevice(
        useDevice,
        videoRef.current,
        (result, err) => {
          if (result) {
            const raw = result.getText();
            console.log('✅ Barcode scanned:', raw);

            setScannedData(raw);

            const parsed = parseBCBP(raw);

            // ✅ Run validations
            const validationErrors = validateBCBP(parsed, allowedAirlines);
            if (validationErrors.length > 0) {
              console.error("❌ Validation failed:", validationErrors);
              setError(validationErrors.join(" "));
              setShowErrorPopup(true); // 🔔 show popup instead of redirect
              codeReaderRef.current.reset();
              return; // stop further processing
            }

            setParsedData(parsed);

            const now = new Date();
            const date = now.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
            const time = now.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });

            setFormData({
              flightNo: parsed.flightNumber || '',
              sqNo: parsed.sequenceNumber || '',
              date,
              time,
              airline: parsed.airline || '',
            });

            // ✅ Stop scanner after successful read
            codeReaderRef.current.reset();
          }

          if (err && err.name !== 'NotFoundException') {
            console.error('Unexpected scan error:', err);
          }
        },
        { formats: [BarcodeFormat.PDF_417] }
      );
    } catch (err) {
      console.error(err);
      setError('Error starting camera: ' + err.message);
    }
  };



  function formatDateToSQL(ukDate) {
    const [day, month, year] = ukDate.split('/');
    return `${year}-${month}-${day}`;
  }

  const handleSubmit = async () => {
    console.log('SUBMIT button clicked');
    console.log('Form data:', formData);

    const hbrUniqueId = localStorage.getItem('hbrUniqueId');
    console.log('HBR Unique ID:', hbrUniqueId);

    if (!hbrUniqueId) {
      setError('No HBR record found. Please complete HBR check first.');
      return;
    }

    try {
      const response = await fetch('https://starskan.com/api/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          rec_scancheckid: formData.flightNo,
          rec_admincode: localStorage.getItem('companyCode'),
          sys_airlinecode: formData.airline,
          sys_terminal: localStorage.getItem('terminal'),
          rec_flightnum: formData.flightNo,
          rec_sqnum: formData.sqNo,
          rec_ckdate: formatDateToSQL(formData.date),
          rec_cktime: formData.time,
          hbrUniqueId: hbrUniqueId,
        }),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error('Failed to submit data');
      }

      const result = await response.json();
      console.log('Success:', result.message);
      navigate('/welcome');
    } catch (error) {
      console.error('Submit error:', error);
      setError('Failed to submit data: ' + error.message);
    }
  };

  return (
    <div
      style={{
        maxWidth: 400,
        margin: 'auto',
        padding: 20,
        backgroundColor: '#f0f0f5',
        borderRadius: 10,
        fontFamily: "'Arial Black', Arial, sans-serif",
        color: '#000',
        textAlign: 'center',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      }}
    >
      <img
        src="/arl-logo.png"
        alt="ARL Aviation Services Logo"
        style={{
          width: '100%',
          maxWidth: 250,
          marginBottom: 20,
          display: 'block',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      />

      <div>
        <video
          ref={videoRef}
          style={{
            width: 'calc(100% - 40px)',
            height: 'calc(100% - 40px)',
            objectFit: 'cover',
            borderRadius: 8,
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
          muted
          playsInline
          autoPlay
        />
      </div>

      {/* ✅ Error popup modal */}
      {showErrorPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 10,
              maxWidth: 400,
              textAlign: "center",
            }}
          >
            <h2 style={{ color: "red" }}>Scan Error</h2>
            <p>{error}</p>
            <button
              onClick={() => {
                setShowErrorPopup(false);
                navigate("/welcome"); // 👈 redirect after closing popup
              }}
              style={{
                backgroundColor: "#e53935",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                marginTop: 10,
                fontWeight: "bold",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          textAlign: 'left',
          fontWeight: 'bold',
          fontSize: 16,
          marginTop: 20,
          marginBottom: 20,
        }}
      >
        {[
          { label: 'FLIGHT NO.', name: 'flightNo', value: formData.airline + ' ' + formData.flightNo },
          { label: 'SQ NO.', name: 'sqNo', value: formData.sqNo },
          { label: 'DATE', name: 'date', value: formData.date },
          { label: 'TIME', name: 'time', value: formData.time },
          { label: 'AL CODE', name: 'airline', value: formData.airline },
        ].map(({ label, name, value }) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ flexBasis: '40%', fontWeight: 'bold' }}>{label}</label>
            <input
              type="text"
              name={name}
              value={value}
              readOnly
              style={{
                flexGrow: 1,
                padding: 5,
                fontWeight: 'normal',
                fontSize: 16,
                borderRadius: 4,
                border: '1px solid #ccc',
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
              }}
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!formData.flightNo || !formData.airline}
        style={{
          backgroundColor: (!formData.flightNo || !formData.airline) ? '#ccc' : '#2e7d32',
          color: 'white',
          fontWeight: 'bold',
          fontSize: 24,
          padding: '15px 0',
          width: '100%',
          border: 'none',
          borderRadius: 6,
          cursor: (!formData.flightNo || !formData.airline) ? 'not-allowed' : 'pointer',
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          opacity: (!formData.flightNo || !formData.airline) ? 0.6 : 1,
        }}
      >
        SUBMIT
      </button>
    </div>
  );
}

// ✅ Updated BCBP Parser
function parseBCBP(bcbp) {
  const formatCode = bcbp[0];
  const numberOfLegs = parseInt(bcbp[1], 10);
  const passengerName = bcbp.substring(2, 22).trim();
  const bookingReference = bcbp.substring(22, 29).trim();

  // --- FIX: correct indices for airport codes ---
  const from = bcbp.substring(30, 33).trim(); // Departure airport (3 chars)
  const to   = bcbp.substring(33, 36).trim(); // Destination airport (3 chars)

  const airline = bcbp.substring(36, 38).trim();
  const flightNumber = bcbp.substring(39, 44).trim();
  const flightDate = bcbp.substring(44, 47); // Julian day (001–366)
  const compartmentCode = bcbp[47];
  const seatNumber = bcbp.substring(48, 52).replace(/^0+/, '');
  const sequenceNumber = bcbp.substring(52, 56).replace(/^0+/, '');
  const passengerStatus = bcbp.substring(56, 59);

  return {
    formatCode,
    numberOfLegs,
    passengerName,
    bookingReference,
    from,
    to,
    airline,
    flightNumber,
    flightDate,
    seatNumber,
    sequenceNumber,
    compartmentCode,
    passengerStatus,
  };
}

// ✅ Updated Validator
function validateBCBP(parsed, allowedAirlines) {
  const errors = [];

  console.log('parsed.from', parsed.from);
  console.log('parsed.airline', parsed.airline);

 

  // --- Validation 1: Date validation ---
  if (parsed.flightDate) {
    try {
      const julianDate = parseInt(parsed.flightDate, 10);
      const currentYear = new Date().getFullYear();

      // Construct local date (Julian day → Gregorian)
      const gregorianDate = new Date(currentYear, 0, julianDate);

      // Normalize to ignore timezones
      gregorianDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Debug logs (local format, not UTC ISO)
      console.log('parsed.flightDate (Julian):', parsed.flightDate);
      console.log('Calculated Gregorian (local):', gregorianDate.toLocaleDateString("en-GB"));
      console.log('Today (local):', today.toLocaleDateString("en-GB"));

      // if (gregorianDate.getTime() !== today.getTime()) {
      //   errors.push("Invalid boarding date: must match today.");
      // }
    } catch (error) {
      console.error('Error parsing flight date:', error);
    }
  }

  // --- Validation 2: Departure must be LHR ---
  if (parsed.from !== "LHR") {
    console.log('parsed.from', parsed.from);
    errors.push("Invalid airport: must be Heathrow (LHR).");
  }

   // --- Validation 3: Airline must be allowed in current terminal ---
   if (!allowedAirlines.includes(parsed.airline)) {
    console.log('parsed.airline', parsed.airline);
    const currentTerminal = localStorage.getItem('terminal') || 'current terminal';
    errors.push(`Invalid airline: ${parsed.airline} not allowed in ${currentTerminal}.`);
  }

  return errors;
}


// ✅ Updated BCBP parser
// function parseBCBP(bcbp) {
//   const formatCode = bcbp[0];
//   const numberOfLegs = parseInt(bcbp[1], 10);
//   const passengerName = bcbp.substring(2, 22).trim();
//   const bookingReference = bcbp.substring(22, 29).trim();
//   const from = bcbp.substring(29, 32).trim();;
//   const to = bcbp.substring(32, 35).trim();;
//   const airline = bcbp.substring(36, 38).trim();;
//   const flightNumber = bcbp.substring(39, 44).trim();
//   const flightDate = bcbp.substring(44, 47); // Julian date format
//   const compartmentCode = bcbp[47];
//   const seatNumber = bcbp.substring(48, 52).replace(/^0+/, '');
//   const sequenceNumber = bcbp.substring(52, 56).replace(/^0+/, '');
//   const passengerStatus = bcbp.substring(56, 59);

//   return {
//     formatCode,
//     numberOfLegs,
//     passengerName,
//     bookingReference,
//     from,
//     to,
//     airline,
//     flightNumber,
//     flightDate,
//     seatNumber,
//     sequenceNumber,
//     compartmentCode,
//     passengerStatus,
//   };
// }

// function validateBCBP(parsed, allowedAirlines) {
//   const errors = [];

//   // --- Validation 1: Date validation ---
//   if (parsed.flightDate) {
//     try {
//       // Convert Julian date to Gregorian date
//       const julianDate = parseInt(parsed.flightDate, 10);
//       const currentYear = new Date().getFullYear();
//       const gregorianDate = new Date(currentYear, 0, julianDate); // January 1st + julianDate - 1

//       // If the calculated date is in the past, it might be for next year
//       if (gregorianDate < new Date()) {
//         gregorianDate.setFullYear(currentYear + 1);
//       }

//       const today = new Date();
//       today.setHours(0, 0, 0, 0); // Reset time to start of day
//       gregorianDate.setHours(0, 0, 0, 0);

//       if (gregorianDate.getTime() !== today.getTime()) {
//         console.log('parsed.flightDate (Julian):', parsed.flightDate);
//         console.log('Calculated Gregorian date:', gregorianDate.toISOString().slice(0, 10));
//         console.log('Today:', today.toISOString().slice(0, 10));
//         errors.push("Invalid boarding date: must match today.");
//       }
//     } catch (error) {
//       console.error('Error parsing flight date:', error);
//       // Skip date validation if parsing fails
//     }
//   }

//   // --- Validation 2: Destination must be LHR ---
//   if (parsed.from !== "LHR") {
//     console.log('parsed.from',parsed.from);
//     errors.push("Invalid airport: must be Heathrow (LHR).");
//   }

//   // --- Validation 3: Airline must be allowed in current terminal ---
//   if (!allowedAirlines.includes(parsed.airline)) {
//     console.log('parsed.airline',parsed.airline);
//     const currentTerminal = localStorage.getItem('terminal') || 'current terminal';
//     errors.push(`Invalid airline: ${parsed.airline} not allowed in ${currentTerminal}.`);
//   }

//   return errors;
// }

export default BarcodePDF417Scanner;
